/**
 * RFC 9449 DPoP (Demonstrating Proof of Possession) proof generation, for
 * exactly one caller: accessRequestsClient.ts's activate(). The server
 * (resolveActivateCNFGen, admin_access_requests.go /
 * selfservice_access_requests.go) refuses to mint a privileged PAM access
 * token unless the activation request proves possession of a DPoP key or
 * an mTLS client certificate — a browser has no client certificate, so
 * DPoP is the only sender-constraining mechanism available here.
 *
 * A fresh ECDSA P-256 keypair is generated PER PROOF, not cached or reused
 * across calls: activation is a one-shot action, and nothing later in this
 * flow presents the resulting privileged access_token (the browser never
 * reads it out of the activate response), so there is no benefit to
 * persisting the key — and generating fresh removes any key-lifecycle or
 * storage concern entirely.
 *
 * The proof format is fixed by the server's validator (dpop.go,
 * ValidateDPoP/parseDPoPHeaders): typ "dpop+jwt", an asymmetric alg (ES256
 * here — the simplest choice, and what crypto.subtle produces directly),
 * an embedded EC JWK public key, and htm/htu/iat/jti claims. htu MUST be
 * scheme://host/path with NO query string (requestHTU in dpop.go strips
 * the query when deriving the expected value server-side).
 */

interface DPoPProofClaims {
  htm: string
  htu: string
  iat: number
  jti: string
}

/** Base64url-encode (no padding) raw bytes. */
function base64url(bytes: ArrayBuffer | Uint8Array): string {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  let bin = ''
  arr.forEach((b) => {
    bin += String.fromCharCode(b)
  })
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

/** Base64url-encode a value as compact JSON (no whitespace). */
function base64urlJSON(value: unknown): string {
  return base64url(new TextEncoder().encode(JSON.stringify(value)))
}

/**
 * generateDPoPProof builds and signs a DPoP proof JWT for exactly one HTTP
 * request — htm/htu identify which request the proof is for, and the
 * server rejects any mismatch (ErrDPoPMethodMismatch/ErrDPoPURIMismatch).
 * Returns the compact JWS string; the caller sends it as the `DPoP`
 * request header.
 */
export async function generateDPoPProof(htm: string, htu: string): Promise<string> {
  const keyPair = (await crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    true,
    ['sign', 'verify']
  )) as CryptoKeyPair

  const publicJWK = await crypto.subtle.exportKey('jwk', keyPair.publicKey)

  // Only the four members the server's JWK header needs — never pass the
  // raw exported JWK through as-is (it may carry key_ops/ext, and MUST
  // never carry the private key material, which only appears if the wrong
  // key half is exported).
  const header = {
    typ: 'dpop+jwt',
    alg: 'ES256',
    jwk: { kty: publicJWK.kty, crv: publicJWK.crv, x: publicJWK.x, y: publicJWK.y },
  }
  const claims: DPoPProofClaims = {
    htm,
    htu,
    iat: Math.floor(Date.now() / 1000),
    jti: crypto.randomUUID(),
  }

  const signingInput = `${base64urlJSON(header)}.${base64urlJSON(claims)}`
  // crypto.subtle's ECDSA signature is the raw IEEE P1363 r||s concatenation
  // per the Web Crypto spec — exactly what JOSE/DPoP expects, no DER
  // conversion needed (unlike e.g. Node's crypto.sign, which defaults to DER).
  const signature = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    keyPair.privateKey,
    new TextEncoder().encode(signingInput)
  )

  return `${signingInput}.${base64url(signature)}`
}
